import { del, get, post } from '@api/api-client';

export interface PmsVoterDto {
  /** The enrollment id — one voter in one edition. */
  id: string;
  editionId: string;
  userId: string;
  email: string;
  name: string;
  surname: string;
  hsid: string | null;
  siteId: string;
  siteCode: string;
  hasPassword: boolean;
  passwordSetAt: string | null;
  createdAt: string;
  /** Votes cast in this enrollment's edition; a deletion takes them with it. */
  voteCount: number;
}

export interface ImportPmsVoterRow {
  email: string;
  hsid: string;
  name?: string;
  surname?: string;
}

export interface FailedPmsRow<T> {
  row: T;
  reason: string;
}

export interface ImportPmsVotersResult {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
  failedRows: FailedPmsRow<ImportPmsVoterRow>[];
  /**
   * Set when no voter of the site shares a HubSpot id with any of its nominees.
   * Both sides are meant to use the same ids, and without an overlap self-vote
   * prevention can never fire — so the absence is worth surfacing.
   */
  hsidOverlapWarning: string | null;
}

export interface AccessLink {
  email: string;
  hsid: string | null;
  siteCode: string;
  /** Raw token, for the "Awards Voting Token" column HubSpot uploads and merges. */
  token: string;
  accessUrl: string;
}

export interface PaginatedPmsVoters {
  data: PmsVoterDto[];
  meta: { totalItems: number; totalPages: number; page: number; pageSize: number };
}

export function listPmsVoters(
  page: number,
  pageSize: number,
  editionId?: string,
  search?: string,
): Promise<PaginatedPmsVoters> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (editionId) params.set('editionId', editionId);
  if (search) params.set('search', search);
  return get<PaginatedPmsVoters>(`/pms-voters?${params.toString()}`);
}

export function importPmsVoters(
  editionId: string,
  rows: ImportPmsVoterRow[],
): Promise<ImportPmsVotersResult> {
  return post<ImportPmsVotersResult>('/pms-voters/import', { editionId, rows });
}

/**
 * Every URL in the result signs its holder in, so the file is a credential list.
 * Scoped to one edition — the campaign — and the download is recorded.
 */
export function exportAccessLinks(editionId: string): Promise<AccessLink[]> {
  const query = `?editionId=${encodeURIComponent(editionId)}`;
  return get<AccessLink[]>(`/pms-voters/access-links-export${query}`);
}

/**
 * Resets a voter's password to a generated one and returns it. The only time it
 * exists in the clear, so it is shown once and never fetched again.
 */
export function resetVoterPassword(enrollmentId: string): Promise<{ password: string }> {
  return post<{ password: string }>(`/pms-voters/${enrollmentId}/reset-password`, {});
}

export interface DeletePmsVoterResult {
  deletedVotes: number;
  /** False while another edition still holds the voter's identity and password. */
  identityDeleted: boolean;
}

/**
 * Removes a voter from the census of one edition, together with the votes they
 * cast in it. Their enrollments in other editions are untouched.
 */
export function deletePmsVoter(enrollmentId: string): Promise<DeletePmsVoterResult> {
  return del<DeletePmsVoterResult>(`/pms-voters/${enrollmentId}`);
}
